import { defineStoryblok } from "@frend-digital/storyblok";
import { notFound } from "next/navigation";
import { rpc } from "#rpc";
import ButtonBlok from "@/components/bloks/atoms/Button/Button";
import {
  HeroCardBlok,
  SimpleHeroBlok,
  TextHeroBlok,
} from "@/components/bloks/atoms/HeroBlok";
import { LinkBlok } from "@/components/bloks/atoms/LinkBlok";
import MediaElement from "@/components/bloks/atoms/MediaElement";
import { ProductBlok } from "@/components/bloks/atoms/ProductBlok";
import { RenewCookieButtonBlok } from "@/components/bloks/atoms/RenewCookieButtonBlok";
import { ReusableContentBlok } from "@/components/bloks/atoms/ReusableContentBlok";
import { ArticleBlok } from "@/components/bloks/layout/ArticleBlok";
import Page from "@/components/bloks/layout/Page/Page";
import ProductPage from "@/components/bloks/layout/ProductPage";
import Accordion from "@/components/bloks/sections/Accordion";
import { ArticleHero } from "@/components/bloks/sections/ArticleHero/ArticleHero";
import { ArticlesBlok } from "@/components/bloks/sections/ArticlesBlok";
import { Content } from "@/components/bloks/sections/Content";
import { ContentCards } from "@/components/bloks/sections/ContentCards";
import { NewHero } from "@/components/bloks/sections/Hero/NewHero";
import Media from "@/components/bloks/sections/Media";
import NewsletterSignUpSection from "@/components/bloks/sections/NewsletterSignupSection/NewsletterSignupSection";
import { PageTopText } from "@/components/bloks/sections/PageTopText";
import { ProductListingBlok } from "@/components/bloks/sections/ProductListingBlok";
import { Quote } from "@/components/bloks/sections/Quote";
import { SideBySide } from "@/components/bloks/sections/SideBySide";
import { StandoutText } from "@/components/bloks/sections/StandoutText";
import Video from "@/components/bloks/sections/Video";
import HeaderBloks from "@/components/bloks/statics/header/HeaderBloks";
import MegaHeaderBloks from "@/components/bloks/statics/header/MegaHeaderBloks";
import type { TypescriptGeneratorStoryblok } from "@/generated/sb/sb-types";
import { ContentCardBlok } from "./sections/ContentCards/components/Card";
import { PopularProductsBlok } from "./sections/ProductListingBlok/PopularProducts";

export type AllBloks = Exclude<
  NonNullable<TypescriptGeneratorStoryblok["blocks"]>[number],
  TypescriptGeneratorStoryblok
>;

const NotFound = () => {
  notFound();
};

defineStoryblok<AllBloks>({
  bloks: {
    /* Fetcher bloks */
    articles: {
      blok: ArticlesBlok,
      fetcher: rpc.articles.getManualArticles,
      getKey: ({ blok }) => blok.articles,
    },
    reusableContent: {
      blok: ReusableContentBlok,
      fetcher: rpc.bloks.reusableContent,
      getKey: ({ blok }) => blok.reusable,
    },
    product: {
      blok: ProductBlok,
      fetcher: rpc.bloks.fetchProductCardById,
      getKey: ({ blok }) => blok.product,
    },
    productListing: {
      blok: ProductListingBlok,
      fetcher: rpc.bloks.products,
      getKey: ({ blok }) => blok.products,
    },
    popularProducts: {
      blok: PopularProductsBlok,
      getKey: ({ blok }) => blok.title,
    },

    /* Nestable bloks */
    accordion: Accordion,
    Article: ArticleBlok,
    articleHero: ArticleHero,
    button: ButtonBlok,
    content: Content,
    contentCard: ContentCardBlok,
    contentSection: ContentCards,
    header: HeaderBloks,
    hero: NewHero,
    heroCard: HeroCardBlok,
    hubspotForm: NotFound,
    link: LinkBlok,
    media: Media,
    mediaElement: MediaElement,
    megaHeader: MegaHeaderBloks,
    newsletterSignupSection: NewsletterSignUpSection,
    page: Page,
    pageTopText: PageTopText,

    productPage: ProductPage,
    quote: Quote,
    reusable: Page,
    sideBySide: SideBySide,
    simpleHero: SimpleHeroBlok,
    standoutText: StandoutText,
    textHero: TextHeroBlok,
    videoSection: Video,
    renewCookieButton: RenewCookieButtonBlok,

    /* Content type bloks */
    account: NotFound,
    accordionitem: NotFound,
    articleCard: NotFound,
    articleCategory: NotFound,
    careGuide: NotFound,
    Customisation: NotFound,
    divider: NotFound,
    favouritesPage: NotFound,
    footer: NotFound,
    footerInfoLinks: NotFound,
    footerSection: NotFound,
    footerSocials: NotFound,
    giftCardPage: NotFound,
    globalConfig: NotFound,
    grid: NotFound,
    login: NotFound,
    megaMenuSection: NotFound,
    menu: NotFound,
    menuSection: NotFound,
    newsletterPopup: NotFound,
    newsletterSignup: NotFound,
    Partial: NotFound,
    plpCategories: NotFound,
    productCategory: NotFound,
    productListingPage: NotFound,
    register: NotFound,
    resetPassword: NotFound,
    return: NotFound,
    returnPage: NotFound,
    retailer: NotFound,
    searchPage: NotFound,
    setNewPassword: NotFound,
    side: NotFound,
    sizeguide: NotFound,
    snackbar: NotFound,
    taggedImage: NotFound,
    teaser: NotFound,
  },
});
